import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tag,
  message,
} from 'antd'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import {
  SPARTACUS_NOTE_DEFAULT_OUTCOME,
  SPARTACUS_NOTE_DEFAULT_TYPE,
  SPARTACUS_NOTE_OUTCOME_OPTIONS,
  SPARTACUS_NOTE_TEXT_MAX_LENGTH,
  SPARTACUS_NOTE_TYPE_OPTIONS,
} from 'src/constants/spartacusNotes'
import {
  createSpartacusVenueNote,
  fetchSpartacusVenueNotes,
  setSpartacusVenueNotePinned,
} from 'src/utils/spartacusApi'
import {
  formatSpartacusNoteAuthorLabel,
  formatSpartacusUkShortDate,
  mapSpartacusVenueNote,
  mapSpartacusVenueNotesResponse,
  sortSpartacusVenueNotes,
} from 'src/utils/spartacusMappers'
import styled from 'styled-components/macro'
import { useSpartacusMobile } from './useSpartacusMobile'

const { TextArea } = Input
const { Option } = Select

const EMPTY_FORM = {
  noteText: '',
  noteType: SPARTACUS_NOTE_DEFAULT_TYPE,
  outcome: SPARTACUS_NOTE_DEFAULT_OUTCOME,
  followUpAt: null,
  pinned: false,
}

function SpartacusVenueNotesModal({
  visible,
  venueId,
  venueName,
  onClose,
  onNotesChanged,
}) {
  const isMobile = useSpartacusMobile()
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [pinningNoteId, setPinningNoteId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const typeOptions = useMemo(
    () =>
      SPARTACUS_NOTE_TYPE_OPTIONS.map(value => (
        <Option key={value} value={value}>
          {value}
        </Option>
      )),
    [],
  )

  const outcomeOptions = useMemo(
    () =>
      SPARTACUS_NOTE_OUTCOME_OPTIONS.map(value => (
        <Option key={value} value={value}>
          {value}
        </Option>
      )),
    [],
  )

  useEffect(() => {
    if (!visible || !venueId) {
      return undefined
    }

    let cancelled = false

    async function loadNotes() {
      setLoading(true)
      setError(null)

      try {
        const response = await fetchSpartacusVenueNotes(venueId, {
          page: 1,
          pageSize: 50,
        })
        if (!cancelled) {
          const mapped = mapSpartacusVenueNotesResponse(response)
          setNotes(sortSpartacusVenueNotes(mapped.notes))
        }
      } catch (err) {
        if (!cancelled) {
          setNotes([])
          setError(err.message || 'Failed to load venue notes')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadNotes()
    setForm(EMPTY_FORM)

    return () => {
      cancelled = true
    }
  }, [visible, venueId])

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    const noteText = String(form.noteText || '').trim()
    if (!noteText) {
      message.warning('Please enter a note before saving')
      return
    }

    setSubmitting(true)
    try {
      const created = await createSpartacusVenueNote(venueId, {
        noteText,
        noteType: form.noteType,
        outcome: form.outcome,
        followUpAt: form.followUpAt ? form.followUpAt.toISOString() : null,
        pinned: Boolean(form.pinned),
      })
      const mapped = mapSpartacusVenueNote(created)
      setNotes(prev =>
        sortSpartacusVenueNotes([
          mapped,
          ...prev.filter(n => n.noteId !== mapped.noteId),
        ]),
      )
      setForm(EMPTY_FORM)
      message.success('Note added')
      if (onNotesChanged) {
        onNotesChanged(venueId, { type: 'created' })
      }
    } catch (err) {
      message.error(err.message || 'Failed to add note')
    } finally {
      setSubmitting(false)
    }
  }

  const handleTogglePin = async note => {
    setPinningNoteId(note.noteId)
    try {
      const updated = await setSpartacusVenueNotePinned(
        venueId,
        note.noteId,
        !note.pinned,
      )
      const mapped = mapSpartacusVenueNote(updated)
      setNotes(prev =>
        sortSpartacusVenueNotes(
          prev.map(item => (item.noteId === mapped.noteId ? mapped : item)),
        ),
      )
      message.success(mapped.pinned ? 'Note pinned' : 'Note unpinned')
      if (onNotesChanged) {
        onNotesChanged(venueId, { type: 'updated' })
      }
    } catch (err) {
      message.error(err.message || 'Failed to update pin')
    } finally {
      setPinningNoteId(null)
    }
  }

  return (
    <StyledModal
      visible={visible}
      title={venueName ? `Customer notes — ${venueName}` : 'Customer notes'}
      width={isMobile ? '100%' : 760}
      onCancel={onClose}
      footer={[
        <Button key='close' onClick={onClose} block={isMobile}>
          Close
        </Button>,
      ]}
    >
      <ModalBody>
        <FormLayout>
          <Form.Item label='Note' required style={{ marginBottom: 12 }}>
            <TextArea
              rows={3}
              maxLength={SPARTACUS_NOTE_TEXT_MAX_LENGTH}
              value={form.noteText}
              placeholder='Add a customer note for the team…'
              onChange={event =>
                handleFieldChange('noteText', event.target.value)
              }
            />
            <CharacterCount aria-live='polite'>
              {form.noteText.length}/{SPARTACUS_NOTE_TEXT_MAX_LENGTH}
            </CharacterCount>
          </Form.Item>

          <RowFields>
            <Form.Item label='Type' style={{ marginBottom: 12, flex: 1 }}>
              <Select
                value={form.noteType}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 140 }}
                onChange={value => handleFieldChange('noteType', value)}
              >
                {typeOptions}
              </Select>
            </Form.Item>
            <Form.Item label='Outcome' style={{ marginBottom: 12, flex: 1 }}>
              <Select
                value={form.outcome}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 180 }}
                onChange={value => handleFieldChange('outcome', value)}
              >
                {outcomeOptions}
              </Select>
            </Form.Item>
          </RowFields>

          <RowFields>
            <Form.Item
              label='Follow-up date'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <DatePicker
                style={{ width: '100%' }}
                value={form.followUpAt}
                allowClear
                format='YYYY-MM-DD'
                onChange={value =>
                  handleFieldChange('followUpAt', value || null)
                }
                disabledDate={current =>
                  current && current < moment().startOf('day')
                }
              />
            </Form.Item>
            <Form.Item label=' ' style={{ marginBottom: 12, flex: 1 }}>
              <Checkbox
                checked={form.pinned}
                onChange={event =>
                  handleFieldChange('pinned', event.target.checked)
                }
              >
                Pin this note
              </Checkbox>
            </Form.Item>
          </RowFields>

          <Button
            type='primary'
            loading={submitting}
            onClick={handleSubmit}
            block={isMobile}
          >
            Add note
          </Button>
        </FormLayout>

        {loading ? (
          <LoadingWrap>
            <Spin />
          </LoadingWrap>
        ) : null}

        {!loading && error ? (
          <Alert type='error' showIcon message={error} />
        ) : null}

        {!loading && !error && notes.length === 0 ? (
          <Empty description='No customer notes yet for this venue' />
        ) : null}

        {!loading && !error && notes.length > 0 ? (
          <Timeline>
            {notes.map(note => (
              <NoteCard key={note.noteId} $pinned={note.pinned}>
                <NoteHeader>
                  <MetaBlock>
                    <AuthorLine>
                      {formatSpartacusNoteAuthorLabel(note.author)}
                    </AuthorLine>
                    <TimestampLine>
                      {formatSpartacusUkShortDate(note.createdAt)}
                      {note.createdAt
                        ? ` · ${moment(note.createdAt).format('HH:mm')}`
                        : ''}
                    </TimestampLine>
                  </MetaBlock>
                  <HeaderActions>
                    {note.pinned ? <Tag color='green'>Pinned</Tag> : null}
                    <Button
                      size='small'
                      loading={pinningNoteId === note.noteId}
                      onClick={() => handleTogglePin(note)}
                    >
                      {note.pinned ? 'Unpin' : 'Pin'}
                    </Button>
                  </HeaderActions>
                </NoteHeader>

                <TagsRow>
                  <Tag>{note.noteType}</Tag>
                  <Tag>{note.outcome}</Tag>
                  {note.followUpAt ? (
                    <Tag color='orange'>
                      Follow-up {formatSpartacusUkShortDate(note.followUpAt)}
                    </Tag>
                  ) : null}
                </TagsRow>

                <NoteText>{note.noteText}</NoteText>
              </NoteCard>
            ))}
          </Timeline>
        ) : null}
      </ModalBody>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }

  .ant-modal-footer .ant-btn + .ant-btn {
    margin-left: 0;
  }

  @media (max-width: 767px) {
    .ant-modal {
      margin: 8px auto;
      max-width: calc(100vw - 16px);
      padding-bottom: 0;
      top: 8px;
    }

    .ant-modal-body {
      max-height: calc(100vh - 220px);
      overflow-y: auto;
    }

    .ant-modal-footer {
      flex-direction: column;
      padding: 12px 16px 16px;
    }
  }
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const FormLayout = styled.div`
  background: ${({ theme }) => theme.colours.smokeWhite || '#f7f7f7'};
  border: 1px solid ${({ theme }) => theme.colours.lightGrey || '#e8e8e8'};
  border-radius: 8px;
  padding: 12px;
`

const RowFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const CharacterCount = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  margin-top: 6px;
  text-align: right;
`

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const NoteCard = styled.div`
  background: ${({ $pinned }) =>
    $pinned ? 'rgba(181, 217, 34, 0.12)' : '#fff'};
  border: 1px solid
    ${({ $pinned, theme }) =>
      $pinned
        ? theme.colours.lettuceGreen || '#b5d922'
        : theme.colours.lightGrey || '#e8e8e8'};
  border-radius: 8px;
  padding: 12px;
`

const NoteHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-bottom: 8px;
`

const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const AuthorLine = styled.div`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  font-weight: 600;
`

const TimestampLine = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
`

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 6px;
`

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const NoteText = styled.div`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
`

export default SpartacusVenueNotesModal
